import {
  ApolloClient,
  gql,
  NormalizedCacheObject,
  useLazyQuery,
  useMutation,
} from '@apollo/client';
import {
  Mutation,
  MutationRescoreVideosAsyncArgs,
  MutationAdminResetVideoScoresArgs,
  Query,
  QueryEstimateRescoreCostArgs,
} from '../types/generated/graphql';

// Sync `rescoreVideos` removed — `rescoreVideosAsync` (BullMQ-backed) covers
// every sample size without blocking the HTTP request. Progress / cancel /
// logs all live on the Background jobs admin page.

const MUTATION_RESCORE_VIDEOS_ASYNC = gql`
  mutation rescoreVideosAsync($input: RescoreVideosInput!) {
    rescoreVideosAsync(input: $input) {
      ok
      messageType
      jobId
    }
  }
`;

export const useRescoreVideosAsync = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'rescoreVideosAsync'>;
  return useMutation<T, MutationRescoreVideosAsyncArgs>(MUTATION_RESCORE_VIDEOS_ASYNC, {
    client,
  });
};

/**
 * Hard reset — wipe every existing AgentScore for the given video ids.
 * Used before rescoring to avoid legacy rows polluting the new gated
 * pipeline's chain-visualization.
 */
const MUTATION_ADMIN_RESET_VIDEO_SCORES = gql`
  mutation adminResetVideoScores($videoIds: [String!]!) {
    adminResetVideoScores(videoIds: $videoIds) {
      videosConsidered
      scoresDeleted
    }
  }
`;

export const useAdminResetVideoScores = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminResetVideoScores'>;
  return useMutation<T, MutationAdminResetVideoScoresArgs>(
    MUTATION_ADMIN_RESET_VIDEO_SCORES,
    {
      client,
      // Force any cached per-video score list to refetch so the UI
      // doesn't keep rendering rows we just deleted.
      refetchQueries: ['getScoresForVideo', 'getAggregateScoreForVideos'],
      awaitRefetchQueries: true,
    },
  );
};

/**
 * Library-wide nuke — deletes EVERY AgentScore row across every video.
 * Used before a full gated-pipeline rescore when we want a guaranteed
 * clean slate (no legacy rows, no half-migrated rows from prior
 * pipeline versions). Irreversible.
 */
const MUTATION_ADMIN_RESET_ALL_VIDEO_SCORES = gql`
  mutation adminResetAllVideoScores {
    adminResetAllVideoScores {
      scoresDeleted
      videosAffected
    }
  }
`;

export const useAdminResetAllVideoScores = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminResetAllVideoScores'>;
  return useMutation<T>(MUTATION_ADMIN_RESET_ALL_VIDEO_SCORES, {
    client,
    // Every per-video list and aggregate query in the app is now
    // stale — refetch anything currently cached.
    refetchQueries: ['getScoresForVideo', 'getAggregateScoreForVideos', 'getAllAgentScores'],
    awaitRefetchQueries: true,
  });
};

/**
 * Library-wide nuke of the `video_summaries` collection. Used after a
 * summarizer code change (e.g. the data-provenance header bump) when
 * existing cached summaries are stale and need to be regenerated on
 * the next scoring pass. Irreversible. Agent scores are untouched —
 * the videos still show their old ratings until rescored.
 */
const MUTATION_ADMIN_RESET_ALL_VIDEO_SUMMARIES = gql`
  mutation adminResetAllVideoSummaries {
    adminResetAllVideoSummaries {
      summariesDeleted
      videosAffected
    }
  }
`;

export const useAdminResetAllVideoSummaries = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminResetAllVideoSummaries'>;
  return useMutation<T>(MUTATION_ADMIN_RESET_ALL_VIDEO_SUMMARIES, {
    client,
  });
};

/**
 * Per-video summary reset. Deletes summary rows for one video so the
 * next scoring pass regenerates it with the current summarizer code.
 * Lighter-weight than the library-wide nuke — admins use this on the
 * video page when they just want to rebuild one video's summary
 * (e.g. after a summarizer bump to see the new provenance header
 * applied to the video they're currently inspecting).
 */
const MUTATION_ADMIN_RESET_VIDEO_SUMMARY = gql`
  mutation adminResetVideoSummary($videoId: String!) {
    adminResetVideoSummary(videoId: $videoId) {
      summariesDeleted
    }
  }
`;

export const useAdminResetVideoSummary = (
  client: ApolloClient<NormalizedCacheObject>,
) => {
  type T = Pick<Mutation, 'adminResetVideoSummary'>;
  return useMutation<T>(MUTATION_ADMIN_RESET_VIDEO_SUMMARY, {
    client,
  });
};

/**
 * Cost forecast for a rescore plan — backed by averages of recent
 * AgentScore rows. Used by the "Rescore entire library" UI so an admin
 * sees $X estimated (+ channel-gated skips) before committing.
 */
const QUERY_ESTIMATE_RESCORE_COST = gql`
  query estimateRescoreCost($input: RescoreCostEstimateInput) {
    estimateRescoreCost(input: $input) {
      videoCount
      channelGatedCount
      llmCallCount
      estimatedCostUSD
      sampleSize
      generatedAt
      breakdown {
        stage
        avgCostUSD
        callsPerVideo
        estimatedCostUSD
      }
    }
  }
`;

export const useEstimateRescoreCost = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'estimateRescoreCost'>;
  return useLazyQuery<T, QueryEstimateRescoreCostArgs>(QUERY_ESTIMATE_RESCORE_COST, {
    client,
    fetchPolicy: 'network-only',
  });
};

/**
 * Preview the review-selector's TOP/MID/BOTTOM bucketing against a
 * pool of videos. Useful before wiring it into a library rescore —
 * you get to see the model's decisions (and their reasons) without
 * committing to any scoring cost beyond the selector call itself.
 */
const QUERY_PREVIEW_REVIEW_SELECTOR = gql`
  query previewReviewSelector($input: ReviewSelectorPreviewInput!) {
    previewReviewSelector(input: $input) {
      classifications {
        videoId
        title
        channelName
        bucket
        confidence
        reason
        selected
      }
      topCount
      midCount
      bottomCount
      selectedIds
      poolSize
      sampleSize
      estimatedCostUSD
      model
      durationMs
      tokenUsage { input output total }
    }
  }
`;

export const usePreviewReviewSelector = (client: ApolloClient<NormalizedCacheObject>) => {
  type T = Pick<Query, 'previewReviewSelector'>;
  return useLazyQuery<T, {input: {videoIds?: string[] | null; poolSize?: number | null; sampleSize?: number | null; topBias?: number | null; bottomBias?: number | null}}>(
    QUERY_PREVIEW_REVIEW_SELECTOR,
    {client, fetchPolicy: 'network-only'},
  );
};
